import { useEffect, useRef } from "react";

// A living WORKSHOP artwork — a luminous schematic build-lattice that echoes the hero's luxury 3D
// network but is themed to construction: gold & teal jewel nodes sit at true depths in space and
// drift gently, linked by right-angle CIRCUIT traces (not organic lines) with bright light pulses
// running along them, as though components are being wired and assembled. Full perspective
// projection + cursor dolly parallax give an ultra-realistic 4D depth. Additive light keeps it
// gallery-grade. Crisp at 4K, sits BEHIND content, respects prefers-reduced-motion.
const GOLD = "212,175,55", GOLD_B = "246,227,166";
const TEAL = "95,211,176", TEAL_B = "178,240,220";

export const WorkshopField = ({ count = 20, className = "", style = {}, speed = 0.15, linkDist = 0.26, interactive = true }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1);

    let W = 0, H = 0, nodes = [], raf = 0, t0 = performance.now();
    let F = 340;
    const Z_MIN = -70, Z_MAX = 640;
    const pointer = { nx: 0, ny: 0, on: false };
    const cam = { x: 0, y: 0 };

    const build = () => {
      W = wrap.clientWidth; H = wrap.clientHeight;
      if (W === 0 || H === 0) return;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      F = Math.max(260, Math.min(W, H) * 0.7);
      const hw = W * 0.6, hh = H * 0.6;
      nodes = Array.from({ length: count }, () => {
        const teal = Math.random() < 0.34;
        const dir = Math.random() * Math.PI * 2;
        const spd = (0.55 + Math.random() * 0.85) * speed;
        return {
          x: (Math.random() * 2 - 1) * hw,
          y: (Math.random() * 2 - 1) * hh,
          z: 0,
          vx: Math.cos(dir) * spd, vy: Math.sin(dir) * spd, spd,
          f1: 0.0004 + Math.random() * 0.0006, p1: Math.random() * 6.283,
          f2: 0.0009 + Math.random() * 0.0012, p2: Math.random() * 6.283,
          turnAmp: 0.018 + Math.random() * 0.022,
          zMid: Z_MIN + Math.random() * (Z_MAX - Z_MIN),
          zAmp: 70 + Math.random() * 160,
          zF: 0.00018 + Math.random() * 0.0004, zP: Math.random() * 6.283,
          baseR: 1.3 + Math.random() * 1.5,
          col: teal ? TEAL : GOLD, bright: teal ? TEAL_B : GOLD_B,
          tw: Math.random() * Math.PI * 2, twSpd: 0.0008 + Math.random() * 0.0013,
          sx: 0, sy: 0, sc: 1,
        };
      });
    };

    const project = (n) => {
      const sc = F / (F + n.z);
      n.sc = Math.max(0.18, Math.min(1.7, sc));
      n.sx = W / 2 + (n.x - cam.x * (n.z / F + 1)) * sc;
      n.sy = H / 2 + (n.y - cam.y * (n.z / F + 1)) * sc;
    };

    const drawNode = (n, boost) => {
      const a = n.sc;
      const tw = 0.8 + 0.2 * Math.sin((performance.now() - t0) * n.twSpd + n.tw);
      const r = n.baseR * a;
      const glow = Math.min(70, r * (4.5 + boost * 3));
      const g = ctx.createRadialGradient(n.sx, n.sy, 0, n.sx, n.sy, glow);
      g.addColorStop(0, `rgba(${n.bright},${Math.min(0.85, (0.42 + boost * 0.3) * a * tw)})`);
      g.addColorStop(0.3, `rgba(${n.col},${(0.28 + boost * 0.2) * a * tw})`);
      g.addColorStop(1, `rgba(${n.col},0)`);
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(n.sx, n.sy, glow, 0, Math.PI * 2); ctx.fill();
      // crisp square-ish core (schematic pad) — tiny rounded rect reads as a component pin
      ctx.fillStyle = `rgba(${n.bright},${Math.min(0.95, 0.72 * a * tw)})`;
      ctx.beginPath(); ctx.arc(n.sx, n.sy, r, 0, Math.PI * 2); ctx.fill();
    };

    const frame = () => {
      if (W === 0 || H === 0) { build(); if (W === 0) { raf = requestAnimationFrame(frame); return; } }
      const time = performance.now() - t0;
      const autoX = Math.sin(time * 0.00015) * 0.5, autoY = Math.cos(time * 0.00012) * 0.35;
      const tgtX = (pointer.on ? pointer.nx : autoX) * W * 0.08;
      const tgtY = (pointer.on ? pointer.ny : autoY) * H * 0.06;
      cam.x += (tgtX - cam.x) * 0.045; cam.y += (tgtY - cam.y) * 0.045;

      const hw = W * 0.6, hh = H * 0.6;
      for (const n of nodes) {
        const turn = (Math.sin(time * n.f1 + n.p1) + 0.6 * Math.sin(time * n.f2 + n.p2)) * n.turnAmp;
        const c = Math.cos(turn), s = Math.sin(turn);
        let vx = n.vx * c - n.vy * s, vy = n.vx * s + n.vy * c;
        if (n.x > hw) vx -= 0.05; else if (n.x < -hw) vx += 0.05;
        if (n.y > hh) vy -= 0.05; else if (n.y < -hh) vy += 0.05;
        const mag = Math.hypot(vx, vy) || 1;
        n.vx = (vx / mag) * n.spd; n.vy = (vy / mag) * n.spd;
        n.x += n.vx; n.y += n.vy;
        n.z = n.zMid + n.zAmp * Math.sin(time * n.zF + n.zP);
        project(n);
      }

      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      ctx.lineJoin = "round"; ctx.lineCap = "round";
      const md = Math.min(W, H) * linkDist;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.sx - b.sx, dy = a.sy - b.sy;
          const dist = Math.hypot(dx, dy);
          if (dist < md) {
            const prox = 1 - dist / md;
            const avg = (a.sc + b.sc) / 2;
            const alpha = prox * prox * (0.1 + avg * 0.22);
            const teal = ((i * 3 + j) % 5) < 2;
            const c = teal ? TEAL : GOLD, cb = teal ? TEAL_B : GOLD_B;
            // right-angle circuit trace (elbow), orientation alternates per pair
            const hFirst = (i + j) % 2 === 0;
            const ex = hFirst ? b.sx : a.sx, ey = hFirst ? a.sy : b.sy;
            ctx.strokeStyle = `rgba(${c},${alpha})`;
            ctx.lineWidth = 0.5 + avg * 0.8;
            ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(ex, ey); ctx.lineTo(b.sx, b.sy); ctx.stroke();
            // light pulse travelling along the trace (component being wired)
            const seg1 = Math.hypot(a.sx - ex, a.sy - ey), seg2 = Math.hypot(ex - b.sx, ey - b.sy);
            const total = seg1 + seg2;
            if (total > 1) {
              const off = ((i * 37 + j * 17) % 100) / 100;
              const p = ((time * 0.00016 + off) % 1) * total;
              let px, py;
              if (p < seg1) { const k = p / (seg1 || 1); px = a.sx + (ex - a.sx) * k; py = a.sy + (ey - a.sy) * k; }
              else { const k = (p - seg1) / (seg2 || 1); px = ex + (b.sx - ex) * k; py = ey + (b.sy - ey) * k; }
              const pr = 1.4 * avg;
              const pg = ctx.createRadialGradient(px, py, 0, px, py, pr * 5);
              pg.addColorStop(0, `rgba(${cb},${0.9 * prox * avg})`);
              pg.addColorStop(1, `rgba(${c},0)`);
              ctx.fillStyle = pg;
              ctx.beginPath(); ctx.arc(px, py, pr * 5, 0, Math.PI * 2); ctx.fill();
            }
          }
        }
      }

      const order = [...nodes].sort((p, q) => q.z - p.z);
      const auraR = Math.min(W, H) * 0.2;
      for (const n of order) {
        let boost = 0;
        if (pointer.on) {
          const px = W / 2 + pointer.nx * W * 0.5, py = H / 2 + pointer.ny * H * 0.5;
          const pd = Math.hypot(n.sx - px, n.sy - py);
          if (pd < auraR) boost += (1 - pd / auraR) * 0.7;
        }
        drawNode(n, boost);
      }

      ctx.globalCompositeOperation = "source-over";
      if (!reduced) raf = requestAnimationFrame(frame);
    };

    build();
    if (reduced) frame(); else raf = requestAnimationFrame(frame);

    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      pointer.on = x >= -40 && y >= -40 && x <= rect.width + 40 && y <= rect.height + 40;
      pointer.nx = (x / rect.width - 0.5) * 2; pointer.ny = (y / rect.height - 0.5) * 2;
    };
    const onLeave = () => { pointer.on = false; };
    if (interactive && !reduced) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("blur", onLeave);
    }
    const ro = new ResizeObserver(() => build());
    ro.observe(wrap);
    return () => {
      cancelAnimationFrame(raf); ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("blur", onLeave);
    };
  }, [count, speed, linkDist, interactive]);

  return (
    <div ref={wrapRef} className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden="true" data-testid="workshop-field" style={style}>
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};

export default WorkshopField;
